import React from 'react';
import {
  Card,
  CardTitle,
  Col,
  Container,
  FormFeedback,
  Input,
  Row,
} from 'reactstrap';
import * as Yup from 'yup';
import moment from 'moment';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import AppBreadcrumb from 'components/App/Breadcrumb';
import AppButton from 'components/App/Button';
import AppCheckboxList from 'components/App/AppCheckboxList';
import AppFlatPickInput from 'components/App/FlatPickInput';
import AppInputNumberValidation from 'components/App/InputNumberValidation';
import AppMeta from 'components/App/Meta';
import FormField from 'components/AddLocation/FormField';

import DATES_OF_WEEK from 'constants/datesOfWeek';
import { LOCATIONS, LOGIN_TYPE, storageKey, themeColor } from 'constants/index';
import INITIAL_VALUE from 'constants/initialValue';
import { PARAM_SETTING } from 'constants/setting';
import { checkHadError, currencyToNumber } from 'utils';
import { updatePriceRoom } from 'utils/validate';

import styles from './Price.module.scss';

const breadcrumbList = [
  {
    name: 'Giá và tình trạng phòng trống',
    isActive: false,
    link: LOCATIONS.LOCATION_LIST,
  },
  {
    name: 'Cập nhật giá bán',
    isActive: true,
    link: '',
  },
];

const newDate = new Date().setHours(0, 0, 0, 0);
const aWeekAgo = new Date(moment(newDate).add(7, 'days'));

const mapRoomOfPlaceToCheckboxItem = (room, roomNamefromSetting, isChecked = false) => {
  const settingLabel = roomNamefromSetting[room.parentRoomName];

  return {
    _id: room._id,
    name: settingLabel || room.name,
    roomName: room.parentRoomName,
    isChecked,
  };
};

function Price(props) {
  const {
    isLoadingUpdatePriceRoom,

    setting,
    placePriceTypes,
    roomOfPlaceList,

    actionGetListRoomsOfPlace,
    actionGetPlacePriceTypes,
    actionUpdatePriceRoom,
  } = props;

  const location = useLocation();
  const history = useHistory();
  const placeId = new URLSearchParams(location.search).get('place');

  const isExtranet = React.useMemo(() =>
    localStorage.getItem(storageKey.LOGIN_TYPE) === LOGIN_TYPE.EXTRANET,
  []);

  const roomNamefromSetting = React.useMemo(() => {
    const roomNamesSetting = setting?.[PARAM_SETTING.ROOM_NAMES];

    if (isEmpty(roomNamesSetting)) {
      return {};
    }

    return roomNamesSetting
      .flatMap((item) => item.children || [])
      .reduce((acc, item) => ({
        ...acc,
        [item.name]: item.vi,
      }), {});
  }, [setting]);

  const filteredPlacePriceTypes = React.useMemo(() => placePriceTypes.filter((item) => item.isDefault || item.pricingStrategy === 'NEW_PRICE'), [
    placePriceTypes,
  ]);

  const validation = useFormik({
    initialValues: {
      startDate: newDate,
      endDate: aWeekAgo,
      weekDays: DATES_OF_WEEK,
      rooms: [],
      priceTypes: [],
      price: '',
    },

    validationSchema: Yup.object(updatePriceRoom),

    onSubmit: (values) => {
      const data = {
        ...values,
        startDate: new Date(values.startDate),
        endDate: new Date(values.endDate.setUTCHours(23, 59, 59)),
        weekDays: values.weekDays
          .filter((item) => item.isChecked)
          .map((item) => +item.value),
        rooms: values.rooms
          .filter((item) => item.isChecked)
          .map((item) => item._id),
        priceTypes: values.priceTypes,
        price: currencyToNumber(values.price),
      };

      actionUpdatePriceRoom({
        placeId,
        data,
      });
    },
  });

  const onChangePrice = React.useCallback((e) => {
    // TODO: Fix decimal
    const regex = /[^0-9]/g;
    const finalValue = e.target.value.trim().replace(regex, '');

    validation.setFieldValue('price', finalValue, true);
  }, []);

  const onChangeStartDate = React.useCallback((date) => {
    if (date.length === 0) {
      validation.setValues((prevState) => ({
        ...prevState,
        startDate: '',
      }));
    } else {
      const checkDateEnd = date[0] > moment(validation.values.endDate).subtract(7, 'days');
      if (checkDateEnd) {
        validation.setValues((prevState) => ({
          ...prevState,
          endDate: '',
        }));
      }

      validation.setValues((prevState) => ({
        ...prevState,
        startDate: date[0],
      }));
    }
  }, [validation.values.endDate]);

  const onChangeEndDate = React.useCallback((date) => {
    validation.setValues((prevState) => ({
      ...prevState,
      endDate: date.length > 0 ? date[0] : '',
    }));
  }, []);

  const onToggleCheckedDateInWeek = React.useCallback((e) => {
    validation.setValues((values) => ({
      ...values,
      weekDays: values.weekDays.map((item) => {
        if (item.value === +e.target.value) {
          return {
            ...item,
            isChecked: e.target.checked,
          };
        }
        return item;
      }),
    }));
  }, []);

  const onToggleSelectAllDate = React.useCallback((e) => {
    validation.setFieldValue('weekDays', validation.values.weekDays.map((item) => ({
      ...item,
      isChecked: +e.target.value === 1,
    })));
  }, [validation.values]);

  const buildRoomsFormValues = React.useCallback((getIsChecked) => (
    roomOfPlaceList.map((room) => {
      const prevRoom = validation.values.rooms.find((item) => item._id === room._id);
      const isChecked = getIsChecked(room, prevRoom);

      return mapRoomOfPlaceToCheckboxItem(room, roomNamefromSetting, isChecked);
    })
  ), [roomNamefromSetting, roomOfPlaceList, validation.values.rooms]);

  const roomCheckboxList = React.useMemo(() => {
    if (isEmpty(roomOfPlaceList)) {
      return [];
    }

    const checkedById = validation.values.rooms.reduce((acc, item) => {
      acc[item._id] = item.isChecked;
      return acc;
    }, {});

    return roomOfPlaceList.map((room) => mapRoomOfPlaceToCheckboxItem(
      room,
      roomNamefromSetting,
      checkedById[room._id] ?? false,
    ));
  }, [roomNamefromSetting, roomOfPlaceList, validation.values.rooms]);

  const onToggleCheckedRoom = React.useCallback((e) => {
    const toggledRoomId = e.target.originalItem?._id ?? e.target.value;

    validation.setFieldValue(
      'rooms',
      buildRoomsFormValues((room, prevRoom) => (
        room._id === toggledRoomId ? e.target.checked : (prevRoom?.isChecked ?? false)
      )),
      false,
    );
  }, [buildRoomsFormValues]);

  const onCheckedAllRooms = React.useCallback((e) => {
    const isCheckedAll = +e.target.value === 1;
    validation.setFieldValue(
      'rooms',
      buildRoomsFormValues(() => isCheckedAll),
      false,
    );
  }, [buildRoomsFormValues]);

  const onChangePriceType = React.useCallback((e) => {
    validation.setValues((values) => ({
      ...values,
      priceTypes: e.target.checked
        ? [...values.priceTypes, e.target.value]
        : values.priceTypes.filter((priceType) => priceType !== e.target.value),
    }));
  }, []);

  const onCancelUpdateRoom = React.useCallback(() => {
    history.push(`${LOCATIONS.LOCATION_DETAIL.DEFAULT}?place=${placeId}`);
  }, []);

  React.useEffect(() => {
    validation.setFieldValue('rooms', []);
  }, [placeId]);

  React.useEffect(() => {
    if (placeId) {
      actionGetListRoomsOfPlace({ placeId });
      actionGetPlacePriceTypes({ placeId });
    }
  }, [placeId]);

  React.useEffect(() => {
    if (isEmpty(roomOfPlaceList)) {
      return;
    }

    validation.setValues((values) => ({
      ...values,
      rooms: roomOfPlaceList.map((room) => {
        const prevRoom = values.rooms.find((item) => item._id === room._id);

        return mapRoomOfPlaceToCheckboxItem(
          room,
          roomNamefromSetting,
          prevRoom?.isChecked ?? false,
        );
      }),
    }));
  }, [roomOfPlaceList, roomNamefromSetting]);

  React.useEffect(() => {
    if (placePriceTypes.length > 0) {
      validation.setFieldValue('priceTypes', [placePriceTypes[0]?._id]);
    }
  }, [placePriceTypes]);

  return (
    <div className={`page-content ${styles.Page}`}>
      <AppMeta
        name="Cập nhật giá bán"
      />

      <Container fluid>
        <AppBreadcrumb
          pageTitle="Cập nhật giá bán"
          breadcrumbList={breadcrumbList}
        />
        <Card className="Section">
          <CardTitle className="Section__Title">
            Quý vị muốn thay đổi ngày nào?
          </CardTitle>

          <Row>
            <Col md="6" className="mb-24">
              <FormField label="Từ ngày">
                <AppFlatPickInput
                  name="startDate"
                  validation={validation}
                  onChange={onChangeStartDate}
                  options={{
                    minDate: 'today',
                  }}
                />
              </FormField>
            </Col>

            <Col md="6" className="mb-24">
              <FormField label="Đến ngày">
                <AppFlatPickInput
                  name="endDate"
                  validation={validation}
                  onChange={onChangeEndDate}
                  options={{
                    minDate: moment(validation.values.startDate || newDate).toDate(),
                  }}
                />
              </FormField>
            </Col>
          </Row>

          <AppCheckboxList
            name="weekDays"
            list={validation.values.weekDays}
            validation={validation}
            onChecked={onToggleCheckedDateInWeek}
            onToggleCheckedAll={onToggleSelectAllDate}
          />
        </Card>

        <Card className="Section">
          <CardTitle className="Section__Title">
            Quý vị muốn thay đổi giá loại phòng nào?
          </CardTitle>

          <AppCheckboxList
            isVertical
            name="rooms"
            list={roomCheckboxList}
            validation={validation}
            onChecked={onToggleCheckedRoom}
            onToggleCheckedAll={onCheckedAllRooms}
          />
        </Card>

        <Card className="Section">
          <CardTitle className="Section__Title">
            Quý vị muốn áp dụng loại giá nào?
          </CardTitle>
          {checkHadError(validation, 'priceTypes', '') && (
            <FormFeedback type="invalid" className="d-block mb-10">
              {get(validation.errors, 'priceTypes', '')}
            </FormFeedback>
          )}

          <div className="Section__RoomBlock--CheckBox">
            {filteredPlacePriceTypes.map((item) => (
              <label className="Checkbox Checkbox--RoomBlock" key={item._id}>
                <Input
                  type="checkbox"
                  className="Checkbox__Input"
                  value={item._id}
                  checked={validation.values.priceTypes.includes(item._id)}
                  onChange={onChangePriceType}
                />

                <span className="Checkbox__Name">{item.name}</span>
              </label>
            ))}
          </div>
        </Card>

        <Card className="Section" style={{ marginBottom: '22px' }}>
          <CardTitle className="Section__Title">
            Cập nhật giá bán
          </CardTitle>

          <FormField isHiddenLabel>
            <AppInputNumberValidation
              name="price"
              keyName="price"
              placeholder="Nhập giá bán"
              validation={validation}
              isCustomOnChange
              onChange={onChangePrice}
              onBlur={validation.handleBlur}
              showCurrency="VNĐ"
            />
          </FormField>
        </Card>

        <div className={isExtranet ? styles.StickyButtonsExtranet : styles.StickyButtons}>
          <div className={styles.ButtonWrap}>
            <AppButton color={themeColor.SECONDARY} onClick={onCancelUpdateRoom}>
              Hủy
            </AppButton>

            <AppButton
              loading={isLoadingUpdatePriceRoom}
              onClick={validation.handleSubmit}
            >
              Cập nhật
            </AppButton>
          </div>
        </div>
      </Container>
    </div>
  );
}

Price.propTypes = {
  isLoadingUpdatePriceRoom: PropTypes.bool.isRequired,

  setting: PropTypes.instanceOf(Object),
  placePriceTypes: PropTypes.instanceOf(Array),
  roomOfPlaceList: PropTypes.instanceOf(Array),

  actionGetListRoomsOfPlace: PropTypes.func,
  actionGetPlacePriceTypes: PropTypes.func,
  actionUpdatePriceRoom: PropTypes.func,
};

Price.defaultProps = {
  setting: INITIAL_VALUE.OBJECT,
  placePriceTypes: INITIAL_VALUE.ARRAY,
  roomOfPlaceList: INITIAL_VALUE.ARRAY,

  actionGetListRoomsOfPlace: INITIAL_VALUE.FUNCTION,
  actionGetPlacePriceTypes: INITIAL_VALUE.FUNCTION,
  actionUpdatePriceRoom: INITIAL_VALUE.FUNCTION,
};

export default Price;
